import { CommonModule } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  Component,
  NgModule,
  ViewEncapsulation,
  computed,
  effect,
  input,
  output,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfigModule } from '@app/config';
import { AppConfigModule } from '@app/layout/config/app.config.module';
import { NTAPCommonModule } from '@ntap/common';
import { type CoreConfig, NTAPCoreModule } from '@ntap/core';
import { NTAPPrimengModule } from '@ntap/primeng';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { injectHierarchyLevel, type HierarchyNode } from '../queries';
import { AngularQueryDevtools } from '@tanstack/angular-query-devtools-experimental';
import type { Page } from '@app/admin/model/spring-pageable';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';

const isInitialHierarchyLoading = signal(true);
const targetIds = signal<(number | string)[]>([]);

@Component({
  selector: 'app-hierarchy-component',
  template: `
    <div class="flex flex-column gap-2 p-2">
      @for (child of initialData().content; track child.id) {
      <app-hierarchy-node
        [initialData]="child"
        (onClick)="passThroughOnClick($event)"
      />
      }
    </div>
  `
})
export class HierarchyComponent {
  public initialData = input.required<Page<HierarchyNode>>();
  public loading = input.required<boolean>();
  public targetIds = input.required<(number | string)[]>();
  public onItemSelected = output<number | string>();

  constructor() {
    effect(
      () => {
        targetIds.set(this.targetIds());
      },
      {
        allowSignalWrites: true
      }
    );

    effect(
      () => {
        isInitialHierarchyLoading.set(this.loading() ?? false);
      },
      {
        allowSignalWrites: true
      }
    );
  }

  public passThroughOnClick(id: number | string) {
    this.onItemSelected.emit(id);
  }
}

@Component({
  selector: 'app-hierarchy-node',
  template: `
    <div class="flex flex-column gap-2">
      <div
        (click)="emmitOnClick()"
        class="flex p-2 gap-2 align-items-center"
        [pTooltip]="
          isJedinicaRezidualnoObrisana()
            ? 'Jedinica gradiva je rezidualno obrisana'
            : ''
        "
        tooltipPosition="top"
        [ngClass]="{
          'text-gray-500': initialData().rezidualniPodaci,
          'cursor-pointer': !isJedinicaRezidualnoObrisana(),
          'hover:bg-gray-100': !isJedinicaRezidualnoObrisana()
        }"
      >
        <span class="py-2" [ngClass]="isTarget() ? 'font-bold' : ''">{{
          initialData().title
        }}</span>
        @if (hasChildren()) {
        <p-button
          class="align-self-center"
          [icon]="icon()"
          [rounded]="true"
          [text]="true"
          (onClick)="toggleShowChildren($event)"
        />
        }
      </div>

      @if (childItemsAreOpened()) {
      <app-hierarchy-children
        [parentId]="initialData().id"
        [initialPage]="page()"
        (onClick)="passThroughOnClick($event)"
      />
      }
    </div>
  `
})
export class HierarchyNodeComponent {
  public initialData = input.required<HierarchyNode>();
  public onClick = output<number | string>();
  public isJedinicaRezidualnoObrisana = computed(
    () => this.initialData()?.rezidualniPodaci ?? false
  );

  public page = computed(
    () => this.initialData()?.children.pageable.pageNumber ?? 0
  );

  public isTarget = computed(() => targetIds().includes(this.initialData().id));

  public childItemsAreOpened = signal(false);
  public icon = computed(
    () => `pi ${this.childItemsAreOpened() ? 'pi-angle-up' : 'pi-angle-down'}`
  );

  public hasChildren = computed(() => this.initialData().hasChildren);

  constructor() {
    effect(
      () => {
        this.childItemsAreOpened.set(this.initialData().isTarget);
      },
      {
        allowSignalWrites: true
      }
    );
  }

  public emmitOnClick() {
    if (this.isJedinicaRezidualnoObrisana()) {
      return;
    }
    this.onClick.emit(this.initialData().id);
  }

  public passThroughOnClick(id: number | string) {
    this.onClick.emit(id);
  }

  public toggleShowChildren(event: MouseEvent) {
    event.stopPropagation();
    this.childItemsAreOpened.update((curr) => !curr);
  }
}

@Component({
  selector: 'app-hierarchy-children',
  template: `
    @if (isLoading()) {
    <app-hierarchy-children-skeleton />
    } @else { @if (hasChildren()) {
    <div class="ml-4 flex flex-column gap-1 p-2 border-left-1">
      @for (child of hierarchy.query.data()?.content; track child.id) {
      <app-hierarchy-node
        [initialData]="child"
        (onClick)="passThroughOnClick($event)"
      />
      }
      <p-paginator
        *ngIf="total() > 5"
        [rows]="rows()"
        [first]="first()"
        [totalRecords]="total()"
        (onPageChange)="onPageChange($event)"
        [showJumpToPageInput]="true"
      />
    </div>
    } }
  `,
  styles: `
    p-paginator > div {
      justify-content: flex-start !important;
      background-color: inherit !important;
    }
`,
  encapsulation: ViewEncapsulation.None
})
export class HierarchyChildrenComponent {
  public parentId = input.required<string | number>();
  public initialPage = input.required<number>();
  public onClick = output<number | string>();

  private userPage = signal<number | null>(null);
  public page = computed(() => this.userPage() ?? this.initialPage());

  public hierarchy = injectHierarchyLevel(() => ({
    id: this.parentId(),
    page: this.page(),
    enabled: !isInitialHierarchyLoading()
  }));
  public hasChildren = computed(
    () => (this.hierarchy.query.data()?.content.length ?? 0) > 0
  );
  public isLoading = computed(() => this.hierarchy.query.isLoading());

  public rows = computed(
    () => this.hierarchy.query.data()?.pageable.pageSize ?? 0
  );
  public first = computed(
    () => this.hierarchy.query.data()?.pageable.offset ?? 0
  );
  public total = computed(
    () => this.hierarchy.query.data()?.totalElements ?? 0
  );

  public passThroughOnClick(id: number | string) {
    this.onClick.emit(id);
  }

  public onPageChange(event: PaginatorState) {
    this.userPage.set(event.page ?? 0);
  }
}

@Component({
  selector: 'app-hierarchy-children-skeleton',
  template: `
    <div class="ml-4 flex flex-column gap-2 p-2 pl-3 border-left-1">
      <p-skeleton width="30rem" height="3rem" />
      <p-skeleton width="30rem" height="3rem" />
      <p-skeleton width="30rem" height="3rem" />
      <p-skeleton width="30rem" height="3rem" />
      <p-skeleton width="30rem" height="3rem" />
    </div>
  `
})
export class HierarchyChildrenSkeletonComponent {}

@NgModule({
  declarations: [
    HierarchyComponent,
    HierarchyNodeComponent,
    HierarchyChildrenComponent,
    HierarchyChildrenSkeletonComponent
  ],
  exports: [HierarchyComponent],
  imports: [
    CommonModule,
    FormsModule,
    StyleClassModule,
    AppConfigModule,
    TooltipModule,
    AppConfigModule,
    AngularQueryDevtools,

    ButtonModule,
    PaginatorModule,
    SkeletonModule,

    ConfigModule.forRoot({} as CoreConfig),
    NTAPCoreModule.forRoot({} as CoreConfig),
    NTAPCommonModule.forRoot({} as CoreConfig),
    NTAPPrimengModule.forRoot()
  ],
  providers: [provideHttpClient(withInterceptorsFromDi())]
})
export class HierarchyModule {}
